// N2 (2026-05-08, ALGORITHM_FLOW v0.49) — settings/recipes 공유 유틸 / 컴포넌트.
//   본질 design 2 layer 분리. recipe.ingredient → recipe.inventory FK rename.
//   옛 ingredients endpoint 영역 영역 inventory/materials endpoint 영역 영역 영역 영역.
//   옛 14 필드 / image upload / category modal 영역 영역 단순화.

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  inventoryControllerCreateMaterial,
  inventoryControllerListMaterials,
  menuCategoriesControllerCreate,
  menuCategoriesControllerList,
  menuCategoriesControllerRemove,
  menuCategoriesControllerUpdate,
  menusControllerCreateMaster,
  menusControllerUpdate,
  recipesControllerCreate,
  recipesControllerList,
  recipesControllerRemove,
  recipesControllerUpdate,
} from '@dvvb/sdk';

import {
  AlertBanner,
  Button,
  FormField,
  Icon,
  Modal,
  TextInput,
  Textarea,
  useToast,
} from '../../../components/primitives';
import { unwrap, ApiCallError } from '../../../lib/envelope';
import { queryClient } from '../../../lib/queryClient';
import type {
  InventoryMaterialRow,
  MenuCategoryRow,
  MenuRow,
  RecipeRow,
} from '../../../types/api';

interface Envelope<T> {
  items: T[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}

// ────────────────────────────────────────────────────────────────────────────
// 마스터 fetch hooks
// ────────────────────────────────────────────────────────────────────────────

export function useInventoryMaterials() {
  return useQuery({
    queryKey: ['inventory', 'materials', 'list'],
    queryFn: async () => {
      const r = await inventoryControllerListMaterials({
        query: { limit: '500' } as never,
      });
      return unwrap<Envelope<InventoryMaterialRow>>(r);
    },
  });
}

export function useMenuCategories() {
  return useQuery({
    queryKey: ['menu-categories', 'list'],
    queryFn: async () => {
      const r = await menuCategoriesControllerList();
      return unwrap<Envelope<MenuCategoryRow>>(r);
    },
  });
}

// ────────────────────────────────────────────────────────────────────────────
// 헬퍼
// ────────────────────────────────────────────────────────────────────────────

/** Decimal string("0.05") → percent string("5"). 빈 값/null → "" */
export function lossRateToPercent(s: string | null | undefined): string {
  if (s == null || s === '') return '';
  const n = Number(s);
  if (!Number.isFinite(n)) return '';
  return String(Math.round(n * 10000) / 100);
}

/** percent string("5") → Decimal number(0.05). 빈 값 → undefined */
export function percentToLossRate(s: string): number | undefined {
  if (s.trim() === '') return undefined;
  const n = Number(s);
  if (!Number.isFinite(n)) return undefined;
  return Math.round((n / 100) * 10000) / 10000;
}

/**
 * N2 (2026-05-08) — 한 메뉴의 cogs 계산.
 *   effective cost = quantity × (1 + lossRate) × inventory.cost.
 *   lossRate fallback: recipe.lossRate → 0 (= 옛 ingredient.defaultLossRate 폐기).
 */
export function computeRecipeCost(
  recipes: RecipeRow[],
  inventoryById: Map<string, InventoryMaterialRow>,
): { totalCost: number; missing: number } {
  let total = 0;
  let missing = 0;
  for (const r of recipes) {
    const inv = inventoryById.get(r.inventoryId);
    if (!inv) {
      missing += 1;
      continue;
    }
    const qty = Number(r.quantity ?? 0);
    const cost = Number(inv.cost ?? 0);
    const lossRate = Number(r.lossRate ?? 0);
    if (
      !Number.isFinite(qty) ||
      !Number.isFinite(cost) ||
      !Number.isFinite(lossRate)
    ) {
      continue;
    }
    total += qty * (1 + lossRate) * cost;
  }
  return { totalCost: Math.round(total * 100) / 100, missing };
}

// ────────────────────────────────────────────────────────────────────────────
// 재료 검색 input (debounce 300ms ILIKE on local list + dropdown + 키보드)
// ────────────────────────────────────────────────────────────────────────────

export interface InventorySearchInputProps {
  inventory: InventoryMaterialRow[];
  selectedId: string;
  onSelect: (inv: InventoryMaterialRow) => void;
  excludeIds?: Set<string>;
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
  style?: CSSProperties;
  onCreateNew?: (query: string) => void;
}

export function InventorySearchInput({
  inventory,
  selectedId,
  onSelect,
  excludeIds,
  disabled,
  error,
  placeholder = '재료 검색…',
  style,
  onCreateNew,
}: InventorySearchInputProps) {
  const selected = inventory.find((i) => i.id === selectedId);
  const [query, setQuery] = useState(selected?.name ?? '');
  const [debounced, setDebounced] = useState(query);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(selected?.name ?? '');
  }, [selected?.id, selected?.name]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = debounced.toLowerCase();
    let list = inventory;
    if (excludeIds && excludeIds.size > 0) {
      list = list.filter((i) => !excludeIds.has(i.id) || i.id === selectedId);
    }
    if (!q) return list.slice(0, 30);
    return list
      .filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          (i.unit ?? '').toLowerCase().includes(q),
      )
      .slice(0, 30);
  }, [inventory, debounced, excludeIds, selectedId]);

  useEffect(() => {
    setHighlight(0);
  }, [debounced, open]);

  function pick(inv: InventoryMaterialRow) {
    onSelect(inv);
    setQuery(inv.name);
    setOpen(false);
  }

  function triggerCreate() {
    if (!onCreateNew) return;
    onCreateNew(query.trim());
    setOpen(false);
  }

  const createIdx = onCreateNew ? filtered.length : -1;
  const totalLen = filtered.length + (onCreateNew ? 1 : 0);

  return (
    <div ref={wrapRef} style={{ position: 'relative', ...style }}>
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
            setOpen(true);
            return;
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(totalLen - 1, h + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(0, h - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlight === createIdx) {
              triggerCreate();
              return;
            }
            const inv = filtered[highlight];
            if (inv) pick(inv);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '7px 10px',
          fontSize: 13,
          fontFamily: 'var(--font-sans)',
          color: 'var(--fg-1)',
          background: disabled ? 'var(--bg-subtle)' : 'var(--bg-surface)',
          border: `1px solid ${error ? 'var(--danger)' : 'var(--border-base)'}`,
          borderRadius: 'var(--radius-sm)',
          outline: 'none',
        }}
      />
      {open && (filtered.length > 0 || onCreateNew) && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-base)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-md)',
            zIndex: 50,
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          {filtered.length === 0 && onCreateNew && (
            <div
              style={{
                padding: '10px 12px',
                fontSize: 12,
                color: 'var(--fg-3)',
                borderBottom: '1px solid var(--border-weak)',
              }}
            >
              일치하는 재료가 없습니다.
            </div>
          )}
          {filtered.map((inv, i) => {
            const on = i === highlight;
            const sel = inv.id === selectedId;
            return (
              <button
                key={inv.id}
                type="button"
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(inv);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '7px 10px',
                  fontSize: 13,
                  fontFamily: 'var(--font-sans)',
                  textAlign: 'left',
                  background: on
                    ? 'var(--primary-bg)'
                    : sel
                      ? 'var(--bg-subtle)'
                      : 'transparent',
                  border: 'none',
                  borderBottom: '1px solid var(--border-weak)',
                  cursor: 'pointer',
                  color: 'var(--fg-1)',
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontWeight: sel ? 600 : 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {inv.name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                    단위 {inv.unit}
                    {inv.cost
                      ? ` · ₩${Math.round(inv.cost).toLocaleString('ko-KR')}/${inv.unit}`
                      : ''}
                  </div>
                </div>
                {sel && <Icon name="check" size={14} color="var(--primary-strong)" />}
              </button>
            );
          })}
          {onCreateNew && (
            <button
              type="button"
              onMouseEnter={() => setHighlight(createIdx)}
              onMouseDown={(e) => {
                e.preventDefault();
                triggerCreate();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                width: '100%',
                padding: '8px 10px',
                fontSize: 13,
                fontFamily: 'var(--font-sans)',
                textAlign: 'left',
                background:
                  highlight === createIdx ? 'var(--primary-bg)' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--primary-strong)',
                fontWeight: 600,
              }}
            >
              <Icon name="plus" size={14} color="var(--primary-strong)" />
              {query.trim()
                ? `"${query.trim()}" 새 재료로 추가`
                : '+ 새 재료 추가'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// NewInventoryForm — InventorySearchInput 의 "+ 새 재료" 콜백 시 inline 등록 폼.
// ────────────────────────────────────────────────────────────────────────────

const COMMON_UNITS = ['g', 'kg', 'ml', 'L', 'ea', '개', '병', '봉'];

export interface NewInventoryFormProps {
  initialName: string;
  onCancel: () => void;
  onCreated: (inv: InventoryMaterialRow) => void;
}

export function NewInventoryForm({
  initialName,
  onCancel,
  onCreated,
}: NewInventoryFormProps) {
  const toast = useToast();
  const [name, setName] = useState(initialName);
  const [unit, setUnit] = useState('g');
  const [cost, setCost] = useState('');
  const [errors, setErrors] = useState<{ name?: string; unit?: string }>({});

  useEffect(() => {
    setName(initialName);
  }, [initialName]);

  const createMu = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        name: name.trim(),
        unit: unit.trim(),
      };
      if (cost.trim()) {
        const c = Number(cost);
        if (Number.isFinite(c) && c >= 0) body.cost = c;
      }
      const r = await inventoryControllerCreateMaterial({ body: body as never });
      return unwrap<{ item: InventoryMaterialRow }>(r);
    },
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ['inventory', 'materials', 'list'] });
      toast.success(`재료 "${res.item.name}" 추가됨`);
      onCreated(res.item);
    },
    onError: (e) => {
      const err = e as ApiCallError;
      if (err.code === 'CONFLICT') {
        toast.error('같은 이름의 재료가 이미 있습니다.');
      } else {
        toast.error(err.message ?? '재료 추가 실패');
      }
    },
  });

  function submit() {
    const next: typeof errors = {};
    if (!name.trim()) next.name = '이름을 입력하세요.';
    if (!unit.trim()) next.unit = '단위를 입력하세요.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    createMu.mutate();
  }

  return (
    <div
      style={{
        marginTop: 8,
        padding: 12,
        background: 'var(--bg-subtle)',
        border: '1px solid var(--border-base)',
        borderRadius: 'var(--radius-sm)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-1)' }}>
        새 재료 등록
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 100px 100px',
          gap: 8,
          alignItems: 'flex-end',
        }}
      >
        <FormField label="이름" required error={errors.name}>
          <TextInput
            value={name}
            onChange={setName}
            placeholder="예: 파스타면"
            error={!!errors.name}
          />
        </FormField>
        <FormField label="단위" required error={errors.unit}>
          <input
            list="dvvb-common-units"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            placeholder="g"
            style={{
              width: '100%',
              padding: '7px 10px',
              fontSize: 13,
              fontFamily: 'var(--font-sans)',
              color: 'var(--fg-1)',
              background: 'var(--bg-surface)',
              border: `1px solid ${errors.unit ? 'var(--danger)' : 'var(--border-base)'}`,
              borderRadius: 'var(--radius-sm)',
              outline: 'none',
            }}
          />
          <datalist id="dvvb-common-units">
            {COMMON_UNITS.map((u) => (
              <option key={u} value={u} />
            ))}
          </datalist>
        </FormField>
        <FormField label="단가 (원)">
          <TextInput
            type="number"
            value={cost}
            onChange={setCost}
            placeholder="(선택)"
          />
        </FormField>
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={createMu.isPending}
        >
          취소
        </Button>
        <Button
          variant="strong"
          size="sm"
          onClick={submit}
          disabled={createMu.isPending}
        >
          {createMu.isPending ? '저장 중…' : '추가 + 자동 선택'}
        </Button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// CategoryManagerModal — 회사 카테고리 list/edit/delete UI.
// ────────────────────────────────────────────────────────────────────────────

export interface CategoryManagerModalProps {
  open: boolean;
  onClose: () => void;
  categories: MenuCategoryRow[];
}

export function CategoryManagerModal({
  open,
  onClose,
  categories,
}: CategoryManagerModalProps) {
  const toast = useToast();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (!open) {
      setDrafts({});
      setNewName('');
    }
  }, [open]);

  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['menu-categories', 'list'] }),
      queryClient.invalidateQueries({ queryKey: ['menus', 'master'] }),
      queryClient.invalidateQueries({ queryKey: ['menus', 'list'] }),
    ]);
  }

  const updateMu = useMutation({
    mutationFn: async (args: { id: string; name: string }) => {
      const r = await menuCategoriesControllerUpdate({
        path: { id: args.id },
        body: { name: args.name } as never,
      });
      return unwrap(r);
    },
    onSuccess: async (_res, vars) => {
      await refresh();
      setDrafts((d) => {
        const next = { ...d };
        delete next[vars.id];
        return next;
      });
      toast.success('카테고리 이름을 수정했습니다.');
    },
    onError: (e) => {
      const err = e as ApiCallError;
      if (err.code === 'CONFLICT') {
        toast.error('같은 이름의 카테고리가 이미 있습니다.');
      } else {
        toast.error(err.message ?? '수정 실패');
      }
    },
  });

  const removeMu = useMutation({
    mutationFn: async (id: string) => {
      const r = await menuCategoriesControllerRemove({ path: { id } });
      return unwrap<{ deleted: boolean; id: string; releasedMenuCount?: number }>(r);
    },
    onSuccess: async (res) => {
      await refresh();
      const released = res?.releasedMenuCount ?? 0;
      if (released > 0) {
        toast.success(
          `카테고리를 삭제했습니다. 메뉴 ${released}개의 카테고리가 비워졌습니다.`,
        );
      } else {
        toast.success('카테고리를 삭제했습니다.');
      }
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '삭제 실패');
    },
  });

  const createMu = useMutation({
    mutationFn: async (n: string) => {
      const r = await menuCategoriesControllerCreate({
        body: { name: n.trim() } as never,
      });
      return unwrap<{ item: MenuCategoryRow }>(r);
    },
    onSuccess: async () => {
      await refresh();
      setNewName('');
      toast.success('카테고리를 추가했습니다.');
    },
    onError: (e) => {
      const err = e as ApiCallError;
      if (err.code === 'CONFLICT') {
        toast.error('같은 이름의 카테고리가 이미 있습니다.');
      } else {
        toast.error(err.message ?? '추가 실패');
      }
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="카테고리 관리"
      size="md"
      footer={
        <Button variant="secondary" onClick={onClose}>
          닫기
        </Button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 11, color: 'var(--fg-3)', lineHeight: 1.5 }}>
          회사 메뉴 분류를 추가·수정·삭제합니다.
        </div>
        {categories.length === 0 ? (
          <div
            style={{
              padding: '20px',
              textAlign: 'center',
              fontSize: 12,
              color: 'var(--fg-3)',
              background: 'var(--bg-subtle)',
              border: '1px dashed var(--border-base)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            카테고리가 없습니다. 아래에서 추가하세요.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {categories.map((c) => {
              const draft = drafts[c.id];
              const dirty = draft !== undefined && draft !== c.name;
              const value = draft ?? c.name;
              const busy =
                (updateMu.isPending && updateMu.variables?.id === c.id) ||
                (removeMu.isPending && removeMu.variables === c.id);
              const used = c.menuCount ?? 0;
              return (
                <div
                  key={c.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 90px auto auto',
                    gap: 6,
                    alignItems: 'center',
                  }}
                >
                  <TextInput
                    value={value}
                    onChange={(v) => setDrafts((d) => ({ ...d, [c.id]: v }))}
                    disabled={busy}
                  />
                  <div style={{ textAlign: 'center', fontSize: 11 }}>
                    {used > 0 ? `${used}개` : '미사용'}
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => updateMu.mutate({ id: c.id, name: value.trim() })}
                    disabled={!dirty || !value.trim() || busy}
                  >
                    저장
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (window.confirm(`카테고리 "${c.name}" 을 삭제할까요?`)) {
                        removeMu.mutate(c.id);
                      }
                    }}
                    disabled={busy}
                    style={{ color: 'var(--danger)' }}
                  >
                    삭제
                  </Button>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ paddingTop: 12, borderTop: '1px solid var(--border-weak)' }}>
          <FormField label="새 카테고리 추가">
            <div style={{ display: 'flex', gap: 6 }}>
              <TextInput
                value={newName}
                onChange={setNewName}
                placeholder="예: 파스타"
                style={{ flex: 1 }}
              />
              <Button
                variant="strong"
                size="sm"
                disabled={!newName.trim() || createMu.isPending}
                onClick={() => createMu.mutate(newName)}
              >
                추가
              </Button>
            </div>
          </FormField>
        </div>
      </div>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// MenuRecipeForm — 메뉴+레시피 통합 폼 (Modal 안)
// ────────────────────────────────────────────────────────────────────────────

export interface RecipeRowDraft {
  recipeId: string;
  inventoryId: string;
  quantity: string;
  unit: string;
  lossRatePercent: string;
}

export interface MenuRecipeFormProps {
  open: boolean;
  onClose: () => void;
  menu: MenuRow | null;
  inventory: InventoryMaterialRow[];
  categories: MenuCategoryRow[];
  initialRecipes: RecipeRow[];
  mode: 'menu' | 'recipe';
}

export function MenuRecipeForm({
  open,
  onClose,
  menu,
  inventory,
  categories,
  initialRecipes,
  mode,
}: MenuRecipeFormProps) {
  const toast = useToast();
  const isEdit = !!menu;

  const [name, setName] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [rows, setRows] = useState<RecipeRowDraft[]>([]);
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [manageCatOpen, setManageCatOpen] = useState(false);
  const [newInv, setNewInv] = useState<{ idx: number; query: string } | null>(null);
  const [errors, setErrors] = useState<{
    name?: string;
    unitPrice?: string;
    rows?: string;
  }>({});

  useEffect(() => {
    if (!open) return;
    setName(menu?.name ?? '');
    setUnitPrice(menu?.unitPrice ? String(Math.round(Number(menu.unitPrice))) : '');
    setCategoryId(menu?.categoryId ?? '');
    setDescription(menu?.description ?? '');
    setRows(
      initialRecipes.map((r) => ({
        recipeId: r.id,
        inventoryId: r.inventoryId,
        quantity: r.quantity ?? '',
        unit: r.inventory?.unit ?? r.unit ?? '',
        lossRatePercent: lossRateToPercent(r.lossRate),
      })),
    );
    setRemovedIds(new Set());
    setErrors({});
    setShowNewCat(false);
    setNewCatName('');
    setManageCatOpen(false);
    setNewInv(null);
  }, [open, menu?.id, menu?.name, menu?.unitPrice, menu?.categoryId, menu?.description, initialRecipes]);

  function addRow() {
    setRows((prev) => [
      ...prev,
      { recipeId: '', inventoryId: '', quantity: '', unit: '', lossRatePercent: '' },
    ]);
  }

  function updateRow(idx: number, patch: Partial<RecipeRowDraft>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function removeRow(idx: number) {
    setRows((prev) => {
      const r = prev[idx];
      if (r?.recipeId) {
        setRemovedIds((s) => new Set(s).add(r.recipeId));
      }
      return prev.filter((_, i) => i !== idx);
    });
  }

  const createCatMu = useMutation({
    mutationFn: async (n: string) => {
      const r = await menuCategoriesControllerCreate({
        body: { name: n.trim() } as never,
      });
      return unwrap<{ item: MenuCategoryRow }>(r);
    },
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ['menu-categories', 'list'] });
      setCategoryId(res.item.id);
      setShowNewCat(false);
      setNewCatName('');
      toast.success('카테고리를 추가했습니다.');
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '카테고리 추가 실패');
    },
  });

  const saveMu = useMutation({
    mutationFn: async () => {
      let menuId: string;
      if (isEdit) {
        menuId = menu!.id;
        const body: Record<string, unknown> = { name: name.trim() };
        if (unitPrice.trim()) body.unitPrice = Number(unitPrice);
        body.categoryId = categoryId || undefined;
        body.description = description;
        const r = await menusControllerUpdate({
          path: { id: menuId },
          body: body as never,
        });
        unwrap(r);
      } else {
        const body: Record<string, unknown> = { name: name.trim() };
        if (unitPrice.trim()) body.unitPrice = Number(unitPrice);
        if (categoryId) body.categoryId = categoryId;
        if (description.trim()) body.description = description;
        const r = await menusControllerCreateMaster({ body: body as never });
        const env = unwrap<{ item: { id: string } }>(r);
        menuId = env.item.id;
      }

      const ops: Promise<unknown>[] = [];
      for (const r of rows) {
        if (!r.inventoryId || !r.quantity) continue;
        const qty = Number(r.quantity);
        if (!Number.isFinite(qty) || qty <= 0) continue;
        const lossRate = percentToLossRate(r.lossRatePercent);
        if (r.recipeId) {
          ops.push(
            recipesControllerUpdate({
              path: { id: r.recipeId },
              body: {
                quantity: qty,
                ...(lossRate !== undefined ? { lossRate } : {}),
              } as never,
            }).then(unwrap),
          );
        } else {
          ops.push(
            recipesControllerCreate({
              path: { menuId },
              body: {
                inventoryId: r.inventoryId,
                quantity: qty,
                ...(lossRate !== undefined ? { lossRate } : {}),
              } as never,
            }).then(unwrap),
          );
        }
      }
      for (const rid of removedIds) {
        ops.push(recipesControllerRemove({ path: { id: rid } }).then(unwrap));
      }
      await Promise.all(ops);
      return { menuId };
    },
    onSuccess: async () => {
      toast.success(isEdit ? '메뉴를 수정했습니다.' : '메뉴를 추가했습니다.');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['menus', 'master'] }),
        queryClient.invalidateQueries({ queryKey: ['recipes'] }),
      ]);
      onClose();
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '저장 실패');
    },
  });

  function submit() {
    const next: typeof errors = {};
    if (!name.trim()) next.name = '메뉴명을 입력하세요.';
    if (unitPrice.trim()) {
      const p = Number(unitPrice);
      if (!Number.isFinite(p) || p < 0) next.unitPrice = '0 이상의 정수를 입력하세요.';
    }
    const validRows = rows.filter((r) => r.inventoryId && r.quantity);
    if (rows.length > 0 && validRows.length === 0) {
      next.rows = '재료 row 가 모두 미완성입니다. 최소 1개를 완성하거나 모두 삭제하세요.';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    saveMu.mutate();
  }

  const inventoryById = useMemo(
    () => new Map(inventory.map((i) => [i.id, i])),
    [inventory],
  );
  const usedIds = new Set(rows.map((r) => r.inventoryId).filter(Boolean));

  const title =
    mode === 'menu'
      ? isEdit
        ? '메뉴 편집'
        : '메뉴 추가 (메뉴 + 레시피)'
      : isEdit
        ? '레시피 편집'
        : '레시피 추가';

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={title}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={onClose} disabled={saveMu.isPending}>
              취소
            </Button>
            <Button variant="strong" onClick={submit} disabled={saveMu.isPending}>
              {saveMu.isPending ? '저장 중…' : isEdit ? '수정' : '추가'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 160px',
              gap: 12,
            }}
          >
            <FormField label="메뉴명" required error={errors.name}>
              <TextInput
                value={name}
                onChange={setName}
                placeholder="예: 명란 파스타"
                error={!!errors.name}
              />
            </FormField>
            <FormField label="기본가 (원)" error={errors.unitPrice}>
              <TextInput
                type="number"
                value={unitPrice}
                onChange={setUnitPrice}
                placeholder="예: 12000"
                error={!!errors.unitPrice}
              />
            </FormField>
          </div>

          <FormField label="카테고리">
            {showNewCat ? (
              <div style={{ display: 'flex', gap: 6 }}>
                <TextInput
                  value={newCatName}
                  onChange={setNewCatName}
                  placeholder="새 카테고리명"
                  style={{ flex: 1 }}
                />
                <Button
                  variant="strong"
                  size="sm"
                  disabled={!newCatName.trim() || createCatMu.isPending}
                  onClick={() => createCatMu.mutate(newCatName)}
                >
                  추가
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowNewCat(false);
                    setNewCatName('');
                  }}
                >
                  취소
                </Button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 6 }}>
                <select
                  value={categoryId}
                  onChange={(e) => {
                    if (e.target.value === '__new__') {
                      setShowNewCat(true);
                      return;
                    }
                    setCategoryId(e.target.value);
                  }}
                  style={{
                    flex: 1,
                    padding: '7px 10px',
                    fontSize: 13,
                    fontFamily: 'var(--font-sans)',
                    color: categoryId ? 'var(--fg-1)' : 'var(--fg-3)',
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-base)',
                    borderRadius: 'var(--radius-sm)',
                    outline: 'none',
                  }}
                >
                  <option value="">선택 안 함</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                  <option value="__new__">+ 새 카테고리</option>
                </select>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setManageCatOpen(true)}
                >
                  관리
                </Button>
              </div>
            )}
          </FormField>

          <FormField label="설명" hint="선택 사항">
            <Textarea
              value={description}
              onChange={setDescription}
              placeholder="메뉴 설명 (선택)"
              rows={2}
            />
          </FormField>

          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600 }}>
                재료 ({rows.length}개)
              </div>
              <Button variant="secondary" size="sm" onClick={addRow}>
                + 재료 추가
              </Button>
            </div>
            {errors.rows && (
              <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 8 }}>
                {errors.rows}
              </div>
            )}
            {rows.length === 0 ? (
              <div
                style={{
                  padding: '20px',
                  textAlign: 'center',
                  fontSize: 12,
                  color: 'var(--fg-3)',
                  background: 'var(--bg-subtle)',
                  border: '1px dashed var(--border-base)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                재료가 없습니다. 위 "+ 재료 추가" 로 시작하세요.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {rows.map((row, i) => {
                  const inv = inventoryById.get(row.inventoryId);
                  const exclude = new Set(usedIds);
                  exclude.delete(row.inventoryId);
                  const showNewIng = newInv?.idx === i;
                  return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(180px, 2fr) 90px 60px 90px 32px',
                          gap: 6,
                          alignItems: 'center',
                        }}
                      >
                        <InventorySearchInput
                          inventory={inventory}
                          selectedId={row.inventoryId}
                          excludeIds={exclude}
                          onSelect={(picked) => {
                            updateRow(i, {
                              inventoryId: picked.id,
                              unit: picked.unit,
                              lossRatePercent: row.lossRatePercent,
                            });
                          }}
                          onCreateNew={(q) => setNewInv({ idx: i, query: q })}
                        />
                        <TextInput
                          type="number"
                          value={row.quantity}
                          onChange={(v) => updateRow(i, { quantity: v })}
                          placeholder="0"
                          style={{ textAlign: 'right' }}
                        />
                        <div
                          style={{
                            padding: '7px 10px',
                            fontSize: 13,
                            color: inv ? 'var(--fg-2)' : 'var(--fg-3)',
                            background: 'var(--bg-subtle)',
                            border: '1px solid var(--border-weak)',
                            borderRadius: 'var(--radius-sm)',
                            textAlign: 'center',
                            minHeight: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {inv?.unit ?? '—'}
                        </div>
                        <TextInput
                          type="number"
                          value={row.lossRatePercent}
                          onChange={(v) => updateRow(i, { lossRatePercent: v })}
                          placeholder="0"
                          style={{ textAlign: 'right' }}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeRow(i)}
                          style={{ color: 'var(--danger)', padding: '4px 6px' }}
                        >
                          <Icon name="x" size={14} />
                        </Button>
                      </div>
                      {showNewIng && (
                        <NewInventoryForm
                          initialName={newInv!.query}
                          onCancel={() => setNewInv(null)}
                          onCreated={(picked) => {
                            updateRow(i, {
                              inventoryId: picked.id,
                              unit: row.unit || picked.unit,
                              lossRatePercent: row.lossRatePercent,
                            });
                            setNewInv(null);
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {saveMu.isError && (
            <AlertBanner
              tone="danger"
              message={(saveMu.error as ApiCallError).message ?? '저장 실패'}
            />
          )}
        </div>
      </Modal>
      <CategoryManagerModal
        open={manageCatOpen}
        onClose={() => setManageCatOpen(false)}
        categories={categories}
      />
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// useMenuRecipes
// ────────────────────────────────────────────────────────────────────────────

export function useMenuRecipes(menuId: string | null) {
  return useQuery({
    queryKey: ['recipes', { menuId }],
    enabled: !!menuId,
    queryFn: async () => {
      const r = await recipesControllerList({
        path: { menuId: menuId! },
        query: { limit: '200' } as never,
      });
      return unwrap<Envelope<RecipeRow>>(r);
    },
  });
}
